<!-- src/components/LandingPage.vue -->
<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { trackLandingView, trackCreateAccountClick } from "@/utils/tracking";

const router = useRouter();

onMounted(() => {
  trackLandingView();
});

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

// Desktop e mobile vão para a mesma tela de login, onde a pessoa escolhe entre
// cadastro tradicional e Google. No mobile este CTA abria o cadastro por
// formulário direto, escondendo a opção do Google.
function goToLogin() {
  trackCreateAccountClick();
  router.push({ name: "LoginView" });
}

// ── Vídeo demo ────────────────────────────────────────────────────────────────
const howItWorksVideoUrl = "https://youtu.be/OPSDnuI8LuA";
const activeVideo = ref(false);
const videoModalTitle = ref("");
const videoModalSub = ref("");
const scrollYBeforeLock = ref(0);
const bodyPaddingRightBeforeLock = ref("");

function getYouTubeId(url) {
  if (!url) return "";
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtu.be")) return (u.pathname || "").replace("/", "").trim();
    const v = u.searchParams.get("v");
    if (v) return v;
    const parts = (u.pathname || "").split("/").filter(Boolean);
    const embedIdx = parts.indexOf("embed");
    if (embedIdx >= 0 && parts[embedIdx + 1]) return parts[embedIdx + 1];
    return "";
  } catch { return ""; }
}

const howItWorksEmbedUrl = computed(() => {
  const id = getYouTubeId(howItWorksVideoUrl);
  if (id) return `https://www.youtube.com/embed/${id}?autoplay=1&rel=0&modestbranding=1&playsinline=1`;
  return "";
});

function lockBodyScroll() {
  scrollYBeforeLock.value = window.scrollY || 0;
  const sw = window.innerWidth - document.documentElement.clientWidth;
  bodyPaddingRightBeforeLock.value = document.body.style.paddingRight || "";
  if (sw > 0) document.body.style.paddingRight = `${sw}px`;
  document.documentElement.classList.add("modal-open");
  document.body.classList.add("modal-open");
}

function unlockBodyScroll() {
  document.documentElement.classList.remove("modal-open");
  document.body.classList.remove("modal-open");
  document.body.style.paddingRight = bodyPaddingRightBeforeLock.value || "";
  window.scrollTo(0, scrollYBeforeLock.value || 0);
}

function openDemoVideo() {
  videoModalTitle.value = "O Litterae em ação";
  videoModalSub.value = "Demonstração";
  activeVideo.value = true;
}

function closeVideo() {
  activeVideo.value = false;
}

watch(activeVideo, (isOpen) => {
  if (isOpen) {
    lockBodyScroll();
    window.addEventListener("keydown", onVideoKeydown);
  } else {
    unlockBodyScroll();
    window.removeEventListener("keydown", onVideoKeydown);
  }
});

function onVideoKeydown(e) {
  if (e.key === "Escape") closeVideo();
}
// ── FIM Vídeo demo ────────────────────────────────────────────────────────────

const symptoms = [
  "Você lê textos importantes, mas quase nunca discute suas interpretações.",
  "Você faz anotações, mas elas ficam espalhadas e difíceis de recuperar.",
  "Você gostaria de saber como outras pessoas entenderam o mesmo trecho.",
  "Você sente que suas análises poderiam evoluir mais se fossem comentadas.",
  "Você tem ideias boas durante a leitura, mas depois elas perdem força.",
  "Você precisa orientar uma turma específica, mas não quer que a discussão se perca em mensagens, arquivos e grupos improvisados.",
  "Você quer compartilhar comentários sem depender de WhatsApp, arquivos soltos ou mensagens perdidas.",
];

const painCards = [
  {
    title: "Anotações espalhadas",
    desc: "Ideias importantes acabam perdidas em arquivos, cadernos, mensagens ou conversas antigas.",
    icon: "fa-note-sticky",
  },
  {
    title: "Interpretação isolada",
    desc: "Você percebe algo relevante no texto, mas não tem um ambiente adequado para comparar leituras.",
    icon: "fa-user",
  },
  {
    title: "Discussões desorganizadas",
    desc: "Quando a conversa acontece fora do texto, o contexto se perde e a análise fica fragmentada.",
    icon: "fa-comments",
  },
];

const teachingItems = [
  {
    title: "Grupos privados para turmas",
    desc: "Crie um grupo para uma turma, clube ou conjunto específico de alunos e direcione a leitura apenas para essas pessoas.",
    icon: "fa-users-rectangle",
  },
  {
    title: "Atividades com acesso direcionado",
    desc: "Associe uma atividade ao texto e defina quem pode visualizar, participar e anotar naquele espaço de leitura.",
    icon: "fa-lock",
  },
  {
    title: "Colaboração com autoria",
    desc: "Cada nota mantém a autoria identificada, permitindo acompanhar como cada aluno ou participante interpretou o texto.",
    icon: "fa-user-check",
  },
  {
    title: "Intervenção pedagógica no contexto",
    desc: "O professor pode orientar, corrigir, comentar e aprofundar interpretações sem tirar a discussão do texto analisado.",
    icon: "fa-chalkboard-user",
  },
];

const solutionItems = [
  {
    title: "Leia com contexto",
    desc: "Cadastre textos, organize atividades e mantenha o conteúdo no centro da análise.",
    icon: "fa-book-open",
  },
  {
    title: "Crie notas no texto",
    desc: "Registre interpretações, dúvidas e comentários vinculados ao conteúdo que você está lendo.",
    icon: "fa-pen-nib",
  },
  {
    title: "Compartilhe interpretações",
    desc: "Permita que outras pessoas contribuam, comentem e ampliem a leitura.",
    icon: "fa-share-nodes",
  },
  {
    title: "Organize sua evolução",
    desc: "Recupere notas, acompanhe contribuições e construa um histórico organizado de leitura.",
    icon: "fa-layer-group",
  },
];

const howItWorks = [
  {
    num: 1,
    title: "Cadastre ou importe o texto",
    desc: "Coloque o texto no Litterae e organize suas informações principais.",
  },
  {
    num: 2,
    title: "Crie uma atividade de leitura",
    desc: "Defina o contexto da análise e organize o estudo em torno daquele texto.",
  },
  {
    num: 3,
    title: "Faça notas e comentários",
    desc: "Registre suas interpretações, dúvidas e observações diretamente no ambiente de leitura.",
  },
  {
    num: 4,
    title: "Compartilhe e acompanhe",
    desc: "Veja contribuições de outras pessoas e transforme a leitura em construção coletiva.",
  },
];

const audiences = [
  {
    title: "Estudantes",
    desc: "Para organizar leituras, anotações e interpretações durante o estudo.",
    icon: "fa-graduation-cap",
  },
  {
    title: "Escolas e Professores",
    desc: "Para propor textos, criar grupos privados, orientar turmas específicas e acompanhar contribuições dos alunos.",
    icon: "fa-chalkboard-user",
  },
  {
    title: "Homeschoolers",
    desc: "Pais e tutores criam atividades, anotam e dialogam dentro do próprio texto junto com os filhos. A aprendizagem acontece com presença e participação, não apenas supervisão.",
    icon: "fa-people-roof",
  },
  {
    title: "Comentadores",
    desc: "Deixe suas anotações públicas, contribua com leituras de outras pessoas e construa reputação por meio de comentários relevantes e autorais.",
    icon: "fa-pen-nib",
  },
  {
    title: "Pesquisadores",
    desc: "Para registrar interpretações, cruzar ideias e recuperar análises anteriores.",
    icon: "fa-microscope",
  },
  {
    title: "Grupos de leitura",
    desc: "Para transformar cada texto em um espaço de conversa organizada.",
    icon: "fa-users",
  },
  {
    title: "Leitores críticos",
    desc: "Para ir além da leitura silenciosa e construir interpretações mais profundas.",
    icon: "fa-book",
  },
  {
    title: "Criadores de conteúdo",
    desc: "Para organizar ideias extraídas de textos, artigos, livros e materiais técnicos.",
    icon: "fa-lightbulb",
  },
];

const year = computed(() => new Date().getFullYear());
</script>

<template>
  <div class="landing-page">
    <header class="topbar">
      <div class="topbar-inner">
        <a href="#" class="topbar-brand" @click.prevent="scrollToTop">
          <img
            src="/litterae_pena_fundo_transparente.png"
            alt="Litterae"
            class="brand-logo"
          />

          <div class="brand-text">
            <div class="brand-name">Litterae</div>
            <div class="brand-tagline">Você não lê sozinho.</div>
          </div>
        </a>

        <nav class="nav-links" aria-label="Ações da conta">
          <RouterLink
            :to="{ name: 'LoginView' }"
            class="nav-btn nav-btn--ghost nav-btn--login"
          >
            Entrar
          </RouterLink>

          <button
            class="nav-btn nav-btn--solid"
            type="button"
            @click="goToLogin"
          >
            Criar conta
          </button>
        </nav>
      </div>
    </header>

    <main>
      <section class="hero">
        <div class="container hero-inner">
          <div class="hero-copy">
            <h1 class="hero-title">Pare de analisar textos sozinho.</h1>

            <p class="hero-subtitle">
              O Litterae é um ambiente para ler, anotar, organizar e
              compartilhar interpretações de textos com outras pessoas.
            </p>

            <div class="hero-cta">
              <button
                class="nav-btn nav-btn--solid hero-cta-btn"
                type="button"
                @click="goToLogin"
              >
                Criar minha conta gratuitamente
              </button>
            </div>

            <p class="hero-note">
              Ideal para quem estuda, ensina, pesquisa ou cria a partir de
              textos.
            </p>

            <p class="hero-platform-note">💻 Atualmente disponível para computadores.</p>
          </div>
        </div>
      </section>

      <section class="section section-after-hero">
        <div class="container">
          <img
            src="/prints/tela_de_anotacoes.png"
            alt="Tela de leitura e anotações do Litterae"
            loading="lazy"
            class="hero-image"
          />

          <div class="demo-video-wrap">
            <button type="button" class="how-video-cta" @click="openDemoVideo">
              <i class="fa-solid fa-circle-play"></i>
              Ver o Litterae em ação
            </button>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="container narrow">
          <div class="section-head section-head--center">
            <h2>Você lê, interpreta, destaca, anota...</h2>
            <p>
              Mas sente falta de um espaço certo para discutir o que percebeu?
            </p>
          </div>

          <div class="diagnosis-copy">
            <p>
              Analisar textos sozinho pode parecer produtivo no começo. Você lê
              no seu ritmo, marca trechos importantes e tenta organizar suas
              ideias.
            </p>

            <p>
              Mas, depois de um tempo, surge uma dificuldade silenciosa:
              <strong>
                suas interpretações ficam presas só dentro da sua cabeça.
              </strong>
            </p>

            <p>
              Talvez o problema não seja falta de leitura. Talvez seja falta de
              um ambiente certo para transformar análise em conversa.
            </p>
          </div>
        </div>
      </section>

      <section class="section section--alt">
        <div class="container">
          <div class="section-head">
            <h2>Sintomas de quem analisa textos de forma solitária</h2>
            <p>
              Veja se estes pontos fazem sentido para sua rotina de leitura.
            </p>
          </div>

          <div class="symptoms-grid">
            <article
              v-for="(item, idx) in symptoms"
              :key="idx"
              class="symptom-card"
            >
              <span class="symptom-icon" aria-hidden="true">
                <i class="fa-solid fa-check"></i>
              </span>
              <p>{{ item }}</p>
            </article>
          </div>

          <div class="diagnosis-result">
            <p>
              Se você se identificou com vários desses pontos:
            </p>

            <strong>
              você não precisa apenas ler melhor. Você precisa de um espaço para
              pensar junto.
            </strong>
          </div>
        </div>
      </section>

      <section class="section section--vs">
        <div class="container narrow">
          <div class="section-head">
            <h2>PDF e Kindle são ótimos para ler. O Litterae é para interpretar junto.</h2>
          </div>

          <div class="vs-body">
            <p>
              No PDF ou no Kindle, suas anotações ficam presas em você. No Litterae, elas entram numa conversa.
            </p>
            <p>
              Você anota dentro do texto, vê o que outras pessoas perceberam no mesmo trecho, e pode seguir quem chamou sua atenção — acompanhando suas atividades pelo painel geral ou filtrando por nome em atividades e textos. Você também pode filtrar textos por assunto e encontrar leitores estudando os mesmos temas que você.
            </p>
            <p class="vs-closing">
              A diferença não é o formato. É que no Litterae, sua leitura pode virar diálogo — e pode te conectar a quem tem os mesmos interesses que o seu.
            </p>
          </div>
        </div>
      </section>

      <section class="section">
        <div class="container">
          <div class="section-head">
            <h2>O problema real não é a leitura solitária</h2>
            <p>
              O problema é quando a análise fica isolada, sem comentários,
              perguntas, comparação de interpretações ou memória organizada.
            </p>
          </div>

          <div class="cards-3">
            <article
              v-for="(card, idx) in painCards"
              :key="idx"
              class="info-card"
            >
              <div class="info-icon">
                <i class="fa-solid" :class="card.icon"></i>
              </div>

              <h3>{{ card.title }}</h3>
              <p>{{ card.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section section--cream">
        <div class="container split">
          <div class="split-copy">
            <p class="eyebrow">O custo de continuar assim</p>

            <h2>
              Quando a conversa sobre o texto não está organizada, a
              inteligência coletiva se perde.
            </h2>
          </div>

          <div class="cost-list">
            <div class="cost-item">
              <i class="fa-solid fa-circle-dot"></i>
              <span>Você perde comentários importantes.</span>
            </div>

            <div class="cost-item">
              <i class="fa-solid fa-circle-dot"></i>
              <span>Você esquece ideias que já teve.</span>
            </div>

            <div class="cost-item">
              <i class="fa-solid fa-circle-dot"></i>
              <span
                >Você repete análises porque não recupera notas antigas.</span
              >
            </div>

            <div class="cost-item">
              <i class="fa-solid fa-circle-dot"></i>
              <span>
                Você depende de ferramentas que não foram feitas para leitura
                colaborativa.
              </span>
            </div>
          </div>
        </div>
      </section>

      <section class="section teaching-section" id="teaching-space">
        <div class="container">
          <div class="section-head">
            <p class="eyebrow">Ensino com turma específica</p>
            <h2>Crie um espaço privado e direcionado para sua turma</h2>
            <p>
              Um professor nem sempre quer publicar uma análise para todos. Às
              vezes, ele precisa ensinar um texto para várias pessoas
              específicas: uma turma, um grupo de estudo, uma disciplina ou um
              clube de leitura fechado.
            </p>
          </div>

          <div class="teaching-panel">
            <div class="teaching-copy">
              <h3>O texto vira a sala de aula da discussão</h3>
              <p>
                No Litterae, o professor pode criar grupos, associar atividades
                a textos e permitir que apenas os participantes escolhidos
                colaborem naquele espaço. Assim, a conversa não se perde em
                mensagens soltas: cada interpretação fica vinculada ao trecho, à
                atividade, ao aluno e ao contexto da leitura.
              </p>

              <p class="teaching-note">
                A turma participa dentro do espaço conduzido pelo professor, sem
                que cada aluno precise assumir um custo próprio.
              </p>
            </div>

            <div class="teaching-grid">
              <article
                v-for="(item, idx) in teachingItems"
                :key="idx"
                class="info-card teaching-card"
              >
                <div class="info-icon">
                  <i class="fa-solid" :class="item.icon"></i>
                </div>

                <h3>{{ item.title }}</h3>
                <p>{{ item.desc }}</p>
              </article>
            </div>
          </div>
        </div>
      </section>

      <section class="section" id="solution">
        <div class="container">
          <div class="section-head">
            <h2>O Litterae coloca o texto no centro da conversa</h2>
            <p>
              Em vez de espalhar comentários em arquivos, mensagens e cadernos,
              você centraliza texto, notas, atividades, comentários e
              colaboração em uma única experiência.
            </p>
          </div>

          <div class="solution-grid">
            <article
              v-for="(item, idx) in solutionItems"
              :key="idx"
              class="info-card solution-card"
            >
              <div class="info-icon">
                <i class="fa-solid" :class="item.icon"></i>
              </div>

              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </article>
          </div>

          <div class="mid-cta">
            <button
              class="nav-btn nav-btn--solid mid-cta-btn"
              type="button"
              @click="goToLogin"
            >
              Criar minha conta gratuitamente
            </button>
          </div>
        </div>
      </section>

      <section class="section section--alt" id="how-it-works">
        <div class="container">
          <div class="section-head">
            <h2>Como o Litterae funciona</h2>
            <p>
              Um fluxo simples para transformar leitura individual em análise
              compartilhada.
            </p>
          </div>

          <div class="steps-grid">
            <article
              v-for="step in howItWorks"
              :key="step.num"
              class="step-card"
            >
              <div class="step-num">{{ step.num }}</div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section" id="audience">
        <div class="container">
          <div class="section-head">
            <h2>Para quem é o Litterae</h2>
            <p>
              Para quem trabalha, estuda ou cria a partir de textos e quer
              transformar leitura em diálogo.
            </p>
          </div>

          <div class="audience-grid">
            <article
              v-for="(item, idx) in audiences"
              :key="idx"
              class="info-card audience-card"
            >
              <div class="info-icon">
                <i class="fa-solid" :class="item.icon"></i>
              </div>

              <h3>{{ item.title }}</h3>
              <p>{{ item.desc }}</p>
            </article>
          </div>
        </div>
      </section>

      <section class="section section--cream">
        <div class="container two-columns">
          <div class="fit-card fit-card--yes">
            <h2>É para você se...</h2>

            <ul>
              <li>analisa textos com frequência;</li>
              <li>gosta de fazer anotações durante a leitura;</li>
              <li>sente falta de trocar interpretações;</li>
              <li>participa ou quer criar grupos de leitura;</li>
              <li>
                precisa orientar turmas ou grupos privados em torno de textos;
              </li>
              <li>quer organizar melhor suas ideias;</li>
              <li>quer transformar leitura em diálogo.</li>
            </ul>
          </div>

          <div class="fit-card fit-card--no">
            <h2>Não é apenas para...</h2>

            <p>
              O Litterae não é um lugar para guardar arquivos.
              Ele foi pensado para quem quer ir além da leitura passiva.
            </p>

            <p>
              É para quem quer conversar com o texto — e com outras pessoas
              sobre o texto.
            </p>
          </div>
        </div>
      </section>

      <section class="section result-section">
        <div class="container narrow">
          <div class="section-head section-head--center">
            <h2>Transforme cada texto em um espaço vivo de interpretação</h2>
            <p>
              Com o Litterae, suas análises deixam de ficar soltas. Você passa a
              construir um histórico organizado de leitura, notas e
              contribuições.
            </p>
          </div>

          <div class="result-box">
            <p>
              A leitura deixa de ser apenas individual. Ela passa a ser
              compartilhada, comentada e aprofundada.
            </p>

            <button
              class="nav-btn nav-btn--solid result-cta"
              type="button"
              @click="goToLogin"
            >
              Criar minha conta gratuitamente
            </button>

            <p class="result-platform-note">
              O Litterae está em fase de lançamento. Neste momento, a plataforma foi otimizada para uso em computadores, proporcionando uma experiência de leitura e anotação mais confortável. A versão para dispositivos móveis está em desenvolvimento.
            </p>
          </div>
        </div>
      </section>
    </main>

    <Teleport to="body">
      <div
        v-if="activeVideo"
        class="video-modal"
        role="dialog"
        aria-modal="true"
        tabindex="0"
        @click="closeVideo"
        @wheel.prevent
        @touchmove.prevent
      >
        <div class="video-modal-inner" @click.stop>
          <div class="video-modal-head">
            <div class="video-modal-title">
              <span class="video-chip" aria-hidden="true">
                <i class="fa-solid fa-play"></i>
              </span>
              <div>
                <div class="video-title">{{ videoModalTitle }}</div>
                <div class="video-sub">{{ videoModalSub }}</div>
              </div>
            </div>
            <button
              class="video-modal-close"
              type="button"
              @click="closeVideo"
              aria-label="Fechar"
            >
              <i class="fa-solid fa-xmark"></i>
            </button>
          </div>

          <div class="video-frame">
            <iframe
              v-if="howItWorksEmbedUrl"
              :src="howItWorksEmbedUrl"
              title="Demonstração do Litterae"
              frameborder="0"
              allow="autoplay; encrypted-media; picture-in-picture"
              allowfullscreen
              referrerpolicy="strict-origin-when-cross-origin"
              class="embed-video-element"
            ></iframe>
          </div>
        </div>
      </div>
    </Teleport>

    <footer class="footer">
      <div class="container footer-inner">
        <div class="footer-brand-wrap">
          <div class="footer-brand">
            <img
              src="/litterae_pena_fundo_transparente.png"
              alt="Litterae"
              class="footer-logo"
            />

            <div>
              <div class="footer-name">Litterae</div>
              <div class="footer-muted">Você não lê sozinho.</div>
            </div>
          </div>

          <div class="footer-company">© {{ year }} Gradus Tecnologia Ltda</div>
        </div>

        <div class="footer-links">
          <RouterLink :to="{ name: 'TermsOfUse' }" class="footer-link-legal">
            Termos de Uso
          </RouterLink>

          <RouterLink :to="{ name: 'PrivacyPolicy' }" class="footer-link-legal">
            Política de Privacidade
          </RouterLink>
        </div>
      </div>
    </footer>
  </div>
</template>

<style scoped>
@import url("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.2/css/all.min.css");

.landing-page {
  min-height: 100vh;
  background: var(--bg-main);
  color: var(--text-dark);
  font-family: var(--font-family);
}

#solution,
#how-it-works,
#audience,
#teaching-space {
  scroll-margin-top: 120px;
}

.container {
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 0 18px;
}

.narrow {
  max-width: 820px;
}

.section {
  padding: 56px 0;
}

.section-after-hero {
  padding-top: 18px;
  padding-bottom: 18px;
}

.section--alt {
  background: var(--bg-content);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
}

.section--cream {
  background: radial-gradient(
      circle at top left,
      rgba(189, 178, 255, 0.18),
      transparent 32%
    ),
    var(--bg-content);
  border-top: 1px solid var(--border-color);
  border-bottom: 1px solid var(--border-color);
}

.section-head {
  margin-bottom: 24px;
}

.section-head--center {
  text-align: center;
}

.section-head h2 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;
}

.section-head p {
  margin: 8px 0 0;
  color: var(--text-light);
  line-height: 1.6;
}

.section-head .eyebrow {
  margin: 0 0 12px;
}

.section-head:not(.section-head--center) > p:not(.eyebrow),
.diagnosis-copy p,
.symptom-card p,
.info-card p,
.cost-item span,
.teaching-copy p,
.step-card p,
.fit-card p,
.fit-card li {
  text-align: justify;
  text-justify: inter-word;
}

/* ---------- Topbar ---------- */
.topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  background: var(--bg-sidebar-left);
  border-bottom: 1px solid var(--border-color);
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.04);
}

.topbar-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 12px 18px;
  display: flex;
  align-items: center;
  gap: 16px;
}

a.topbar-brand {
  text-decoration: none;
  color: inherit;
}

.topbar-brand {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  cursor: pointer;
}

.brand-logo {
  width: 46px;
  height: 46px;
  object-fit: contain;
  border-radius: 12px;
  flex: 0 0 auto;
}

.brand-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.brand-name {
  font-weight: 800;
  font-size: 1.1rem;
  line-height: 1.1;
  letter-spacing: -0.01em;
}

.brand-tagline {
  font-size: 0.85rem;
  color: var(--text-light);
  line-height: 1.2;
}

.nav-links {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 10px;
}

.nav-btn {
  text-decoration: none;
  font-weight: 800;
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  transition: background-color 0.2s ease-in-out, transform 0.05s ease,
    box-shadow 0.15s ease;
  font-size: 0.95rem;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.nav-btn--ghost {
  background: rgba(255, 255, 255, 0.65);
  color: var(--text-dark);
}

.nav-btn--solid {
  background: var(--brand-light);
  color: var(--text-on-brand);
  border-color: transparent;
}

.nav-btn:hover {
  box-shadow: 0 10px 18px rgba(0, 0, 0, 0.08);
}

.nav-btn:active {
  transform: translateY(1px);
}

.nav-btn:focus,
.nav-btn:focus-visible {
  outline: none;
}

/* ---------- Hero ---------- */
.hero {
  padding: 42px 0 12px;
  background: radial-gradient(
      circle at 15% 10%,
      rgba(189, 178, 255, 0.32),
      transparent 28%
    ),
    radial-gradient(
      circle at 85% 20%,
      rgba(160, 196, 255, 0.24),
      transparent 30%
    ),
    var(--bg-main);
}

.hero-inner {
  display: flex;
  justify-content: center;
}

.hero-copy {
  width: 100%;
  max-width: 900px;
  text-align: center;
  padding: 22px 0;
}

.eyebrow {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 0 12px;
  padding: 7px 12px;
  border-radius: 999px;
  background: rgba(189, 178, 255, 0.22);
  border: 1px solid rgba(189, 178, 255, 0.38);
  color: var(--text-on-brand);
  font-weight: 900;
  font-size: 0.88rem;
}

.hero-title {
  margin: 0;
  font-size: 2.5rem;
  line-height: 1.08;
  letter-spacing: -0.03em;
  font-weight: 600;
}

.hero-subtitle {
  margin: 18px auto 0;
  font-size: 1.08rem;
  line-height: 1.7;
  color: var(--text-light);
  max-width: 720px;
}

.hero-cta {
  margin-top: 22px;
  display: flex;
  justify-content: center;
}

.hero-cta-btn {
  min-height: 48px;
  padding: 13px 18px;
  font-size: 1rem;
}

.hero-note {
  margin: 14px auto 0;
  color: var(--text-light);
  font-size: 0.95rem;
}

.demo-video-wrap {
  margin-top: 16px;
  text-align: center;
}

.how-video-cta {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 9px 14px;
  border-radius: 14px;
  border: 1px solid var(--border-color);
  background: rgba(255, 255, 255, 0.7);
  color: var(--text-dark);
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.how-video-cta:hover {
  transform: translateY(-1px);
  box-shadow: 0 12px 18px rgba(0, 0, 0, 0.08);
}

.how-video-cta i {
  color: var(--text-on-brand);
  opacity: 0.9;
}

.video-modal {
  position: fixed;
  inset: 0;
  z-index: 100000;
  padding: 18px;
  background: rgba(10, 14, 26, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
  touch-action: none;
}

.video-modal-inner {
  width: min(1280px, 96vw);
  max-height: 92vh;
  background: var(--surface);
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.22);
  box-shadow: 0 26px 70px rgba(0, 0, 0, 0.38);
}

.video-modal-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 14px;
  border-bottom: 1px solid var(--border-color);
  background: rgba(255, 255, 255, 0.78);
}

.video-modal-title {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.video-chip {
  width: 42px;
  height: 42px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(189, 178, 255, 0.28);
  border: 1px solid rgba(189, 178, 255, 0.45);
  color: var(--text-on-brand);
  flex: 0 0 auto;
}

.video-chip i { transform: translateX(1px); }

.video-title {
  font-weight: 900;
  letter-spacing: -0.01em;
  line-height: 1.15;
}

.video-sub {
  color: var(--text-light);
  font-size: 0.9rem;
  line-height: 1.2;
}

.video-modal-close {
  width: 38px;
  height: 38px;
  border-radius: 12px;
  border: 1px solid var(--border-color);
  background: rgba(255, 255, 255, 0.7);
  color: var(--text-dark);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: none;
}

.video-modal-close:hover { background: rgba(255, 255, 255, 0.95); }

.video-frame {
  position: relative;
  width: 100%;
  height: min(72vh, calc(96vw * 9 / 16));
  background: #0b0f1a;
  overflow: hidden;
}

.embed-video-element {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: none;
}

.hero-platform-note {
  margin: 8px auto 0;
  color: var(--text-light);
  font-size: 0.8rem;
  opacity: 0.75;
}

.hero-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 18px;
  border: 1px solid var(--border-color);
  box-shadow: 0 18px 42px rgba(0, 0, 0, 0.08);
}

/* ---------- Diagnosis ---------- */
.diagnosis-copy {
  display: grid;
  gap: 14px;
  color: var(--text-light);
  font-size: 1.02rem;
  line-height: 1.75;
}

.diagnosis-copy p {
  margin: 0;
}

.diagnosis-copy strong {
  color: var(--text-dark);
}

.symptoms-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.symptom-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.05);
}

.symptom-icon {
  width: 28px;
  height: 28px;
  border-radius: 999px;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(189, 178, 255, 0.22);
  color: var(--text-on-brand);
  border: 1px solid rgba(189, 178, 255, 0.42);
  font-size: 0.78rem;
}

.symptom-card p {
  margin: 0;
  color: var(--text-light);
  line-height: 1.55;
}

.vs-body {
  max-width: 680px;
  margin: 0 auto;
}

.vs-body p {
  margin: 0 0 16px;
  color: var(--text-light);
  line-height: 1.75;
}

.vs-closing {
  font-weight: 700;
  color: var(--text-dark) !important;
}

.diagnosis-result {
  margin-top: 20px;
  padding: 18px;
  border-radius: 20px;
  background: rgba(189, 178, 255, 0.16);
  border: 1px solid rgba(189, 178, 255, 0.38);
  text-align: center;
}

.diagnosis-result p {
  margin: 0 0 8px;
  color: var(--text-light);
}

.diagnosis-result strong {
  display: block;
  font-size: 1.1rem;
  line-height: 1.45;
}

/* ---------- Cards ---------- */
.cards-3,
.solution-grid,
.audience-grid,
.steps-grid,
.teaching-grid {
  display: grid;
  gap: 12px;
}

.cards-3 {
  grid-template-columns: repeat(3, 1fr);
}

.solution-grid,
.steps-grid,
.teaching-grid {
  grid-template-columns: repeat(4, 1fr);
}

.audience-grid {
  grid-template-columns: repeat(3, 1fr);
}

.info-card,
.step-card,
.fit-card {
  background: var(--surface);
  border: 1px solid var(--border-color);
  border-radius: 18px;
  padding: 16px;
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.05);
}

.info-icon {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(160, 196, 255, 0.35);
  border: 1px solid rgba(160, 196, 255, 0.55);
  margin-bottom: 10px;
  color: var(--text-on-brand);
}

.info-card h3,
.step-card h3 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 900;
}

.info-card p,
.step-card p {
  margin: 6px 0 0;
  color: var(--text-light);
  line-height: 1.6;
}

.solution-card,
.audience-card,
.teaching-card {
  height: 100%;
}

/* ---------- Cost ---------- */
.split {
  display: grid;
  grid-template-columns: 0.95fr 1.05fr;
  gap: 28px;
  align-items: center;
}

.split-copy h2 {
  margin: 0;
  font-size: 2rem;
  line-height: 1.18;
  letter-spacing: -0.03em;
}

.cost-list {
  display: grid;
  gap: 12px;
}

.cost-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  border-radius: 16px;
  background: var(--surface);
  border: 1px solid var(--border-color);
  color: var(--text-light);
  line-height: 1.55;
}

.cost-item i {
  margin-top: 4px;
  color: var(--text-on-brand);
  opacity: 0.72;
}

/* ---------- Teaching ---------- */
.teaching-section {
  background: var(--bg-main);
}

.teaching-panel {
  padding: 18px;
  border-radius: 24px;
  background: radial-gradient(
      circle at top right,
      rgba(189, 178, 255, 0.26),
      transparent 34%
    ),
    white;
  border: 1px solid var(--border-color);
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.06);
}

.teaching-copy {
  max-width: 820px;
  margin-bottom: 18px;
}

.teaching-copy h3 {
  margin: 0;
  font-size: 1.35rem;
  font-weight: 900;
  letter-spacing: -0.02em;
}

.teaching-copy p {
  margin: 8px 0 0;
  color: var(--text-light);
  line-height: 1.7;
}

.teaching-note {
  padding: 12px 14px;
  border-radius: 16px;
  background: rgba(189, 178, 255, 0.14);
  border: 1px solid rgba(189, 178, 255, 0.36);
}

.teaching-card {
  background: rgba(255, 255, 255, 0.86);
}

/* ---------- Steps ---------- */
.step-num {
  width: 44px;
  height: 44px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 900;
  background: rgba(189, 178, 255, 0.22);
  border: 1px solid rgba(189, 178, 255, 0.4);
  color: var(--text-on-brand);
  margin-bottom: 12px;
}

/* ---------- Fit ---------- */
.two-columns {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 18px;
}

.fit-card h2 {
  margin: 0 0 12px;
  font-size: 1.35rem;
  font-weight: 900;
}

.fit-card ul {
  margin: 0;
  padding-left: 20px;
  color: var(--text-light);
  line-height: 1.75;
}

.fit-card p {
  margin: 0;
  color: var(--text-light);
  line-height: 1.7;
}

.fit-card p + p {
  margin-top: 12px;
}

.fit-card--yes {
  border-color: rgba(189, 178, 255, 0.55);
}

.fit-card--no {
  background: rgba(255, 255, 255, 0.72);
}

/* ---------- CTA ---------- */
.mid-cta {
  margin-top: 22px;
  display: flex;
  justify-content: center;
}

.mid-cta-btn,
.result-cta {
  min-height: 46px;
  padding: 12px 18px;
}

.result-section {
  padding-bottom: 66px;
}

.result-box {
  padding: 24px;
  border-radius: 24px;
  background: radial-gradient(
      circle at top right,
      rgba(189, 178, 255, 0.28),
      transparent 36%
    ),
    white;
  border: 1px solid var(--border-color);
  text-align: center;
  box-shadow: 0 14px 34px rgba(0, 0, 0, 0.06);
}

.result-platform-note {
  margin: 20px auto 0 !important;
  font-size: 0.8rem !important;
  opacity: 0.65;
}

.result-box p {
  margin: 0 auto 18px;
  color: var(--text-light);
  line-height: 1.7;
  max-width: 680px;
}

/* ---------- Footer ---------- */
.footer {
  background: var(--bg-sidebar-left);
  border-top: 1px solid var(--border-color);
  padding: 22px 0;
}

.footer-inner {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  align-items: center;
}

.footer-brand-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.footer-brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.footer-logo {
  width: 38px;
  height: 38px;
  object-fit: contain;
  border-radius: 10px;
}

.footer-name {
  font-weight: 900;
  line-height: 1.1;
}

.footer-muted,
.footer-company {
  color: var(--text-light);
  font-size: 0.9rem;
}

.footer-links {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.footer-link-legal {
  color: var(--text-light);
  text-decoration: none;
  font-weight: 800;
}

.footer-link-legal:hover {
  text-decoration: underline;
}

/* ---------- Responsive ---------- */
@media (max-width: 980px) {
  .solution-grid,
  .steps-grid,
  .teaching-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .cards-3,
  .audience-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .split {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .topbar-inner {
    padding: 10px 14px;
  }

  .brand-logo {
    width: 42px;
    height: 42px;
  }

  .brand-name {
    font-size: 1rem;
  }

  .brand-tagline {
    font-size: 0.78rem;
  }

  .nav-btn--login {
    display: none;
  }

  .nav-btn {
    padding: 9px 12px;
    font-size: 0.9rem;
  }

  .hero {
    padding-top: 30px;
  }

  .hero-copy {
    padding: 14px 0;
  }

  .hero-title {
    font-size: 1.9rem;
    line-height: 1.12;
  }

  .hero-subtitle {
    font-size: 1rem;
  }

  .hero-cta-btn {
    width: 100%;
    max-width: 360px;
  }

  .section {
    padding: 42px 0;
  }

  .section-head h2 {
    font-size: 1.45rem;
  }

  .symptoms-grid,
  .solution-grid,
  .steps-grid,
  .teaching-grid,
  .cards-3,
  .audience-grid,
  .two-columns {
    grid-template-columns: 1fr;
  }

  .split-copy h2 {
    font-size: 1.55rem;
  }

  .footer-inner {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 420px) {
  .container {
    padding: 0 14px;
  }

  .nav-links {
    gap: 6px;
  }

  .nav-btn {
    padding: 9px 10px;
  }

  .hero-title {
    font-size: 1.7rem;
    line-height: 1.12;
    letter-spacing: -0.03em;
  }

  .hero-image {
    border-radius: 14px;
  }

  .info-card,
  .step-card,
  .fit-card,
  .symptom-card,
  .teaching-panel,
  .result-box {
    border-radius: 16px;
  }
}
</style>